import { action, autorun, computed, observable, toJS, makeObservable } from 'mobx';
import { apiService } from '../../services/api.service';
import { history } from 'fn-common-ui';
import { modalsStore } from '../modals.store';
import { utilsService } from '../../services/utils.service';

import NewAppNamePlatformStore from './new-app-name-platform.store';
import NewAppDetailsStore from './new-app-details.store';
import NewAppUploadStore from './new-app-upload.store';
import NewAppValidationsStore from './new-app-validations.store';

import _ from 'lodash';

export default class NewAppMasterStore {
	constructor(masterStore) {
		makeObservable(this);
		this.masterStore = masterStore
		this.newAppNamePlatformStore = new NewAppNamePlatformStore(this);
		this.newAppUploadStore = new NewAppUploadStore(this);
		this.newAppDetailsStore = new NewAppDetailsStore(this);
		this.createAlert = masterStore.createAlert.bind(masterStore);
		this.clearAlerts = masterStore.clearAlerts.bind(masterStore);
		this.finishGoingToError = this.finishGoingToError.bind(this);
		this.newAppValidationsStore = new NewAppValidationsStore(this, this.newAppDetailsStore, this.newAppUploadStore);
		this.submissionCancelled = false;
		this.submissionTimeout = null;

		autorun(() => {
			if (this.newAppFormIsDirty) {
				this.saveAndCheckHasBeenClicked = false;
			}
		});
	}

	// FPSE-1821: URL now has: required http, optional s, reqired ://, at least one character or dash followed by a . (the (sub)domains), string of at least 2 characters long (TLD), an optional port, and any number of "/digits" for routes
	URL_REGEX = /^((https?:\/\/)?([\w-]+\.)+?([\w]{2,})(:\d+)?(\/\S*)?)$/i;
	VERSION_REGEX = /^\d+(\.\d+)*$/;
	HYPERLINK_PREFIX = /^https?:\/\//i;
	STORE_LINK_MISSING_ERROR = 'Provide a link to the file here or upload a file above and submit again.'
	BINARY_MISSING_ERROR = 'Please upload your file here and submit it again.'
	MALFORMED_WEBSITE_ERROR = 'Please enter a valid website URL.';
	MALFORMED_VERSION_ERROR = 'Version must consist of alternating numbers (0-9) and periods (.).';
	VERSION_MISMATCH_ERROR = 'Version number must match or be newer than the one in the App Store/Google Play store.';
	REQUIRED_ERROR = 'This is a required field.';
	FILE_SIZE_100MB_ERROR = 'File size exceeds 100MB.';
	FILE_SIZE_250MB_ERROR = 'File size exceeds 250MB.';
	FILE_SIZE_2GB_ERROR = 'Please upload your file here and submit it again.';
	APK_REQUIRED_ERROR = 'Please select an AAB or APK file, or set your platform to iOS to select an IPA file.';
	IPA_REQUIRED_ERROR = 'Please select an IPA file, or set your platform to Android to select an AAB or APK file.';
	ZIP_OR_XLSX_ERROR = 'Invalid file type. ZIP or XLSX files only.';
	PNG_ERROR = 'Please select a PNG file.';
	DIMENSIONS_ERROR = 'Image height and width must be the same.';

	// ACTIONS
	@action unsetTermsAgreement() {
		this.masterStateProps.userAgreedToTerms = false;
	}

	@action cancelSubmission() {
		this.submissionCancelled = true;
		this.showLoadingOverlay = false;
		clearTimeout(this.submissionTimeout);
	}

	@action initNewAppData() {
		return {
			aboutDev: '',
			appFileName: '',
			applicationId: null,
			appName: '',
			app_type: '',
			authorName: '',
			caFormFileName: '',
			caFormFileExtension: '',
			caFormUploadDate: '',
			capabilities: [],
			category: [],
			certifications: [],
			device: '',
			devWebsite: '',
			fileUploadDateTime: '',
			iconImageURLAndDescription: '',
			listingType: '',
			lastSavedDatetime: '', //used in edit mode, but not stored on the server
			longDescription: '',
			mobileURLAndDescription: [],
			platform: '',
			releaseDate: null,
			//removalDate: null, //not used in new-application flow
			tags: '',
			saveId: null,
			searchTags: [],
			segments: [],
			shortDescription: '',
			//status: '', //not used in new-application flow
			storeLink: '',
			storeVersion: '',
			tabletURLAndDescription: [],
			version: '',
			versionNotes: '',
			//withdrawnDate: null, //not used in new-application flow
			imageReordered: false, //used in edit mode, but not stored on the server
			urlType: 'C'
		}
	}

	@action cloneNewAppData = () => {
		//console.log('cloneNewAppData start');
		this.newAppDataClone = _.cloneDeep(toJS(this.newAppData));
		//console.log('cloneNewAppData end');
	};

	@action clearNewAppData() {
		this.newAppData = this.initNewAppData();

		this.masterStateProps.errorsAreDisplayed = false;
		this.masterStateProps.namePlatformIsEditable = true;
		this.masterStateProps.userAgreedToTerms = false;
		this.masterStateProps.isSavingAndChecking = false;
		this.masterStateProps.isPartialSaving = false;

		this.newAppDataClone = {};
		this.isLoading = false;
		this.isGoingToError = false;
		this.saveAndCheckHasBeenClicked = false;
		this.showLoadingOverlay = false;
		this.routeAttemptingToVisit = '';
	}

	@action diffNewAppData() {
		var changedValues = Object.keys(this.newAppData).filter((key) => {
			if (!_.isEqual(toJS(this.newAppData[key]), toJS(this.newAppDataClone[key]))) {
				return key
			}
		});
		return changedValues;
	}

	@action closeAndGotoMyApps() {
		history.push('/my-apps');
	}

	@action disableSaveDialogs() {
		this.unblock();
	}

	@action discardChangesAndLeave() {
		this.newAppData = _.cloneDeep(toJS(this.newAppDataClone));
		this.unblockAfterModal()
	}

	@action unblockAfterModal() {
		this.unblock();
		modalsStore.hideModal('unsavedChanges');
		history.push(this.routeAttemptingToVisit);
	}

	@action enableSaveDialogs() {
		//This action is called in the constructor of the New App pages and App Edit page only.
		this.unblock = history.block((tx) => {
			this.currentRoute = window.location.pathname;
			this.routeAttemptingToVisit = tx.location.pathname;

			// In history v5, call allowTransition() to permit navigation, or do nothing to block it.
			const allowTransition = () => {
				this.unblock();
				tx.retry();
			};

			//Do not block if moving between steps in the New App flow.
			if (this.routeAttemptingToVisit.includes('/new-application')) {
				allowTransition();
				return;
			}

			//Do not block if going from the submission to the app details page.
			if (this.currentRoute.includes('/new-application/app-submit') && this.routeAttemptingToVisit.includes('/app/view')) {
				allowTransition();
				return;
			}

			//Do not block if moving between certain steps in the Edit App flow.
			if (this.currentRoute.includes('/app/edit') && this.routeAttemptingToVisit.includes('/app/review')
				|| this.currentRoute.includes('/app/review') && this.routeAttemptingToVisit.includes('/app/edit')) {
				allowTransition();
				return;
			}

			//Do not block if leaving on Step 0 of the New App flow.
			if (!this.newAppData.appName && !this.newAppData.platform) {
				allowTransition();
				return;
			}

			//Do not block if on New App form or App Edit form, and form is not dirty.
			if (!this.newAppFormIsDirty) {
				allowTransition();
				return;
			}

			//Prompt with Unsaved Changes dialog
			modalsStore.showModal('unsavedChanges');
		});
	}

	formatImageObject = (screen) => {
		return {
			imageURL: screen.imageURL || screen.path, // depends on which API was used to get the image
			description: screen.description,
			id: screen.id,
			isUploading: false,
			progress: 100
		}
	}

	syncImageData = (data) => {
		this.newAppUploadStore.screens.mobile = data.mobileURLAndDescription.map(this.formatImageObject);
		this.newAppUploadStore.screens.tablet = data.tabletURLAndDescription.map(this.formatImageObject);
		this.newAppUploadStore.screens.icon = [];

		if (data.iconImageURLAndDescription.length) {
			this.newAppUploadStore.screens.icon.push({
				imageURL: data.iconImageURLAndDescription,
				isUploading: false,
				progress: 100
			})
		}
	}

	@action updateImageData(deviceType) {
		const newAppDataDeviceKeys = { mobile: 'mobileURLAndDescription', tablet: 'tabletURLAndDescription', icon: 'iconImageURLAndDescription' };
		if (deviceType) {
			const newAppDataDeviceKey = newAppDataDeviceKeys[deviceType];
			this.newAppData[newAppDataDeviceKey] = deviceType === 'icon'
				? this.newAppUploadStore.iconImageURL
				: this.newAppUploadStore.screens[deviceType];
		} else {
			// update all deviceTypes if no single deviceType was listed.
			for (let deviceType in newAppDataDeviceKeys) {
				this.updateImageData(deviceType);
			}
		}
		this.newAppData.imageReordered = true;
	}

	@action loadDraft(saveId) {
		this.masterStateProps.namePlatformIsEditable = false; // reset should happen before attempting to load a draft so that copying an app can go straight to step 0
		const success = (response) => {
			this.newAppDetailsStore.isValidatingFields = false;
			this.masterStateProps.errorsAreDisplayed = false;
			this.isLoading = false;
			response.data.capabilities = response.data.capabilities || []; // TODO: fallback for old apps that don't have capabilities so we don't try to get null.length
			response.data.searchTags = response.data.searchTags || []; // TODO: fallback for old apps that don't have searchTags so we don't try to get null.length
			this.updateAppData(response.data);

			// Populate upload store filenames from loaded draft data
			this.newAppUploadStore.developerChecklist.filename = this.newAppUploadStore.previouslyUploadedDeveloperChecklist.get();
			this.newAppUploadStore.appPackage.filename = this.newAppUploadStore.previouslyUploadedAppPackage.get();

			//Save latest to the clone since it reflects what's on the server
			this.cloneNewAppData();
		};
		const fail = () => {
			this.isLoading = false;
		};

		this.isLoading = true;
		apiService.loadSavedApp(saveId).then(success, fail);
	}

	@action updateAppData(newAppData) {
		Object.keys(newAppData).forEach(key => {
			this.newAppData[key] = newAppData[key];
		});
		this.syncImageData(this.newAppData);
		let fields = this.newAppDetailsStore.inputFields;
		for (let name in fields) {
			if (fields[name].validations.maxChars && this.newAppData[name].length > fields[name].validations.maxChars.criterion) {
				this.newAppData[name] = '';
			}
		}
		return this.newAppData;
	}

	@action storeAppInPartialSaveFormat(appData, versionIndex) {
		// if (versionIndex === -1) versionIndex = 0;
		//console.log('storeAppInPartialSaveFormat - ', JSON.stringify(appData));
		let selectedVersion = appData.versions[versionIndex];
		this.newAppData.urlType = appData.urlType;
		this.newAppData.appName = appData.app_name;
		this.newAppData.platform = appData.platform;
		
		if(selectedVersion || selectedVersion != undefined){
		this.newAppData.aboutDev = selectedVersion.custom_metadata.developer_description;
		this.newAppData.appFileName = selectedVersion.custom_metadata.applicationFileName;
		//this.newAppData.applicationId = selectedVersion.applicationId;
		this.newAppData.app_type = selectedVersion.custom_metadata.app_type;
		this.newAppData.authorName = selectedVersion.author;
		this.newAppData.caFormFileName = utilsService.getFileName(selectedVersion.custom_metadata.caFileName);
		this.newAppData.caFormFileExtension = utilsService.getFileExtension(selectedVersion.custom_metadata.caFileName);
		this.newAppData.caFormUploadDate = selectedVersion.custom_metadata.caFileUploadDateTime;
		this.newAppData.capabilities =  selectedVersion.custom_metadata.capabilities;
		this.newAppData.category = selectedVersion.custom_metadata.category;
		this.newAppData.certifications = selectedVersion.custom_metadata.certifications;
		this.newAppData.device = selectedVersion.custom_metadata.device;
		this.newAppData.devWebsite = selectedVersion.custom_metadata.developer_website;
		this.newAppData.fileUploadDateTime = selectedVersion.custom_metadata.fileUploadDateTime;
		this.newAppData.iconImageURLAndDescription = selectedVersion.icon_path;
		this.newAppData.listingType = selectedVersion.custom_metadata.listingType;
		this.newAppData.longDescription = selectedVersion.custom_metadata.long_description;
		this.newAppData.mobileURLAndDescription = selectedVersion.mobileScreenshots;
		this.newAppData.releaseDate = selectedVersion.custom_metadata.release_date;
		this.newAppData.removalDate = selectedVersion.custom_metadata.removal_date;
		this.newAppData.tags = selectedVersion.custom_metadata.searchText;
		if (selectedVersion.saveId) {
			this.newAppData.saveId = selectedVersion.saveId;
			this.newAppData.applicationId = appData.applicationId;
		}
		this.newAppData.segments = selectedVersion.custom_metadata.user_segment;
		const searchTagList = this.newAppDetailsStore.checkboxFields.searchTags.values.map((tag) => { return tag.display });
		// filter out old tags that are no longer in the searchTags list
		this.newAppData.searchTags = selectedVersion.custom_metadata.search_tags ? selectedVersion.custom_metadata.search_tags.filter((tag) => searchTagList.includes(tag)) : [];
		this.newAppData.shortDescription = selectedVersion.custom_metadata.short_description;
		this.newAppData.statusReason = selectedVersion.custom_metadata.statusReason;
		this.newAppData.status = selectedVersion.custom_metadata.status;
		this.newAppData.storeLink = selectedVersion.custom_metadata.store_link; //Notice the intentional case mismatch from partially saved drafts
		this.newAppData.storeVersion = selectedVersion.custom_metadata.storeVersion;
		this.newAppData.tabletURLAndDescription = selectedVersion.tabletScreenshots;
		this.newAppData.version = selectedVersion.version_num;
		this.newAppData.versionNotes = selectedVersion.version_notes;
		this.newAppData.withdrawnDate = selectedVersion.custom_metadata.withdrawn_date;
	}
		//Process the images
		this.syncImageData(this.newAppData);

		// Populate upload store filenames from loaded app data
		this.newAppUploadStore.developerChecklist.filename = this.newAppUploadStore.previouslyUploadedDeveloperChecklist.get();
		this.newAppUploadStore.appPackage.filename = this.newAppUploadStore.previouslyUploadedAppPackage.get();

		//Save latest to the clone since it reflects what's on the server
		this.cloneNewAppData();
	}

	@action partialSave(optionalAdditionalSuccessFunction) {
		const success = (res) => {

			this.newAppData.imageReordered = false;
			this.newAppData.lastSavedDatetime = res.data.lastSavedDatetime;
			this.masterStateProps.namePlatformIsEditable = false;

			if (this.newAppData.saveId === res.data.saveId) {
				//Save latest to the clone since it reflects what's on the server
				this.cloneNewAppData();
			} else {
				/* Retrieve a fresh copy of the app data after success,
				since the saveId and image paths have changed */
				apiService.loadSavedApp(res.data.saveId).then((savedApp) => {
					this.newAppData = savedApp.data;
					this.syncImageData(savedApp.data);
					this.newAppUploadStore.developerChecklist.filename = this.newAppUploadStore.previouslyUploadedDeveloperChecklist.get();
					this.newAppUploadStore.appPackage.filename = this.newAppUploadStore.previouslyUploadedAppPackage.get();
					this.cloneNewAppData();
					// update the url to reflect new saveId and version, but don't reload the page.
					const currentRoute = window.location.pathname
					const currentRouteBase = currentRoute.slice(0, currentRoute.lastIndexOf('/') + 1 )
					history.replace(currentRouteBase+res.data.saveId)
				});
				this.newAppData.saveId = res.data.saveId;
			}

			if (res.data.applicationId) {
				this.newAppData.applicationId = res.data.applicationId;
			}

			if (optionalAdditionalSuccessFunction) {
				optionalAdditionalSuccessFunction();
			}

			this.masterStateProps.isPartialSaving = false;
		};

		const fail = (error) => {
			this.masterStateProps.isSavingAndChecking = false;
			this.masterStateProps.isPartialSaving = false;

			if(error.response && error.response.status === 406) {
				const errorCode = error.response.data.errorCode;
				if(errorCode === 'DRAFT_LIMIT_EXCEEDED') {
					this.createAlert({
						title: 'Draft Limit Reached',
						message: `You have reached the limit of 15 drafts. Please delete an existing draft before creating another one.`,
						type: 'info'
					});
					window.scrollTo(0, 0);
					return;
				} else if(errorCode === 'VERSION_ALREADY_EXISTS') {
					this.createAlert({
						title: 'Draft Not Saved',
						message: 'Draft could not be saved. There is already an app with this name, platform, and version number. Please input a unique version number or edit the app\'s name or platform.',
						type: 'error'
					});
					window.scrollTo(0, 0);
					this.newAppData.version = '';
					return;
				}
			}

			throw error;
		};

		this.clearAlerts();
		this.masterStateProps.isPartialSaving = true;

		//TODO: Part of fix for FPSE-3598. saveId must not be "null" when draft is first created; Services cannot fix this on their end before end of current sprint.
		if (this.newAppData.saveId === null) {
			this.newAppData.saveId = '';
		}

		apiService.partialSave(this.newAppData).then(success, fail);
	}

	@action updateFile(endpoint, file) {
		if (endpoint === 'uploadDeveloperChecklistFile') {
			this.newAppData.caFormFileName = file.name;
			this.newAppData.caFormFileExtension = file.extension;
			this.newAppData.caFormUploadDate = Date.now();
			this.newAppUploadStore.developerChecklist.filename = `${file.name}.${file.extension}`;
		} else if (endpoint === 'uploadPackage') {
			this.newAppData.appFileName = file.name;
			this.newAppData.fileUploadDateTime = Date.now();
			this.newAppUploadStore.appPackage.filename = file.name;
		}
	}

	@action clearFile(endpoint) {
		if (endpoint === 'uploadDeveloperChecklistFile') {
			this.newAppData.caFormFileName = '';
			this.newAppData.caFormFileExtension = '';
			this.newAppData.caFormUploadDate = '';
			this.newAppUploadStore.developerChecklist.filename = null;
		} else if (endpoint === 'uploadPackage') {
			this.newAppData.appFileName = '';
			this.newAppData.fileUploadDateTime = '';
			this.newAppUploadStore.appPackage.filename = null;
		}
	}

	@action resetSubmissionProcess() {
		this.masterStateProps.namePlatformIsEditable = true;
		this.newAppData = this.initNewAppData();
		this.newAppDetailsStore.resetFields();
		this.newAppUploadStore.resetFields();
		this.submissionCancelled = false;
		this.appSuccessfullyEdited = null;
	}

	@action saveStep(optionalStepNumber, onSuccess) {
		if(optionalStepNumber === 0) {
			if(!this.newAppData.version) {
				this.newAppData.version = this.newAppData.storeVersion;
			}
		}

		const additionalSuccessFunction = () => {
			if(onSuccess) {
				onSuccess();
			}
		};

		this.partialSave(additionalSuccessFunction);
	}

	@action saveAndCheckForErrors(optionalStepNumber) {
		const onSuccess = () => {
			this.newAppDetailsStore.isValidatingFields = true;
			this.goToNextStep();
			this.masterStateProps.isSavingAndChecking = false;
		};

		this.saveAndCheckHasBeenClicked = false;
		this.masterStateProps.isSavingAndChecking = true;
		this.saveStep(optionalStepNumber, onSuccess);
	}

	@action saveStepAndContinue(optionalStepNumber) {
		const onSuccess = () => {
			// don't change route after saving app name/platform
			if(optionalStepNumber !== 0) {
				this.goToNextStep();
			}
		};
		this.saveStep(optionalStepNumber, onSuccess);
	}

	@action saveChangesAndLeave(optionalStepNumber) {
		const onSuccess = () => {
			this.unblockAfterModal();
		}
		this.saveStep(optionalStepNumber, onSuccess);
	}
	
	@action goToNextStep() {
		const pathname = history.location.pathname.split('/new-application/')[1];
		const currentStep = pathname.split('/')[0];
		switch (currentStep) {
			case 'app-details':
				this.pushUrl('app-package');
				break;
			case 'app-package':
				this.pushUrl('app-screenshots');
				break;
			case 'app-screenshots':
				if (this.newAppValidationsStore.errorList.length > 0) {
					// Errors exist. Stay on step 3.
					this.masterStateProps.errorsAreDisplayed = true;
					['package', 'icon', 'mobile', 'tablet'].forEach(device => this.newAppUploadStore.invalidUploadAttempted[device] = false);
				} else {
					this.saveAndCheckHasBeenClicked = true;
					this.pushUrl('app-review');
				}
				break;
			case 'app-review':
				this.pushUrl('app-submit');
				break;
			default:
				break;
		}
	}

	pushUrl(nextStepName) {
		history.push(`/new-application/${nextStepName}/${this.newAppData.saveId}`);
	}

	@action stayAndKeepEditing() {
		modalsStore.hideModal('unsavedChanges');
	}

	@action submitApplication() {
		const versionsSuccess = (res) => {
			const app = res.data.versions.filter((e) => e.custom_metadata.status === 'UNDER_REVIEW')[0];
			const appObject = {
				applicationId : this.newAppData.applicationId,
				version : app.version_num,
				saveId : app.saveId
			}

			this.createAlert({
				title: 'The draft was not submitted successfully.',
				message: `A prior version of the app is still under review. Only one version of an app may be under review at a time. To submit this new version, withdraw version ${app.version_num} or wait until it is approved or rejected. <a href=${utilsService.generateAppPageURI(appObject, 'view')} target="_blank">View version ${app.version_num} <i class="icon-external-site" aria-hidden="true"></i></a>`,
				class: 'error'
			});
			window.scrollTo(0, 0);

			this.showLoadingOverlay = false;
		};

		const versionsFail = (err) => {
			this.showLoadingOverlay = false;
			throw err;
		};

		const submitSuccess = () => {
			const appData = Object.assign({}, this.newAppData); //FPSE 1719 clone app data before changing pages
			let weekCount = "6-7";
			if (/GENERAL/i.test(appData.app_type)) {
				weekCount = 'four';
			}

			this.showLoadingOverlay = false;
			// route has to change before we reset the submission process, but status needs to be blank to avoid hitting an edge case
			this.newAppData.custom_metadata.status = '';
			history.push(utilsService.generateAppPageURI(this.newAppData, 'view'))
			this.resetSubmissionProcess();
			this.createAlert({
				title: 'Success! Your app has been submitted.',
				message: `<strong>${appData.appName} v${appData.version} for ${appData.platform}</strong> will be reviewed by the FirstNet team. You should receive an approval or rejection notification via email within ${weekCount} weeks.`,
				type: 'info'
			});
		};

		const submitFail = (err) => {
			let testMessage = /(...) Cannot be submitted as it is already in review with C&A team/;
			if (testMessage.test(err.response.data.message)) {
				apiService.getAppVersions(this.newAppData.saveId).then(versionsSuccess, versionsFail);
			} else if (err.response.data.errorCode === 'STORE_VERSION_NOT_ALLOWED'){
				this.createAlert({
					title: 'Version Number Mismatch',
					message: 'App could not be submitted. ' + this.VERSION_MISMATCH_ERROR,
					type: 'error'
				});
				window.scrollTo(0, 0);
				return;
			} else {
				throw err;
			}
		};

		this.submissionCancelled = false;
		this.showLoadingOverlay = true;
		window.scrollTo(0, 0);

		this.submissionTimeout = setTimeout(() => {
			if (!this.submissionCancelled) {
				apiService.submitApp(this.newAppData.saveId).then(submitSuccess, submitFail);
			}
		}, 4000);
	}

	@action validateAppUpdate(e) {
		this.newAppDetailsStore.isValidatingFields = true;
		if (this.newAppValidationsStore.editAppHasErrors) {
			e.preventDefault();
			this.masterStateProps.errorsAreDisplayed = true;
		} else {
			this.masterStateProps.errorsAreDisplayed = false;
		}
	}

	@action submitAppUpdate() {
		this.appSuccessfullyEdited = null;
		this.showLoadingOverlay = true;
		window.scrollTo(0, 0);
		let dataForUpdateRequest = {};
		const prepDataForUpdate = () => {

			// gather changed fields
			//console.log("prepDataForUpdate start");
			let changedFields = this.diffNewAppData();
			changedFields.forEach(fieldName => {
				dataForUpdateRequest[fieldName] = this.newAppData[fieldName]
			});

			// Ensure that no matter what, imageReordered is true if any image fields were changed
			const imageFields = ['mobileURLAndDescription', 'tabletURLAndDescription', 'iconImageURLAndDescription']
			if (imageFields.some((field) => changedFields.includes(field))) {
				dataForUpdateRequest.imageReordered = true;
			}

			// add the applicationIdentifier
			if (this.newAppData.saveId) {
				dataForUpdateRequest.saveId = this.newAppData.saveId;
			} else {
				/* FIXME cast applicationId to a number. This is how it's distinguished
				from a saveId on the backend. Ideally, we would always hold the appPsk
				as a number (not a String) during front end manipulations.*/
				dataForUpdateRequest.appPsk = parseInt(this.newAppData.applicationId);
			}

			/* FIXME make segments key singular, to match category. Eventually,
			fix this globally on FE and BE. */
			if (dataForUpdateRequest.segments) {
				dataForUpdateRequest.segment = dataForUpdateRequest.segments;
				delete dataForUpdateRequest.segments;
			}

			//convert iconImageURLAndDescription to iconImage for push API compliance
			if (dataForUpdateRequest.iconImageURLAndDescription) {
				dataForUpdateRequest.iconImage = dataForUpdateRequest.iconImageURLAndDescription;
				delete dataForUpdateRequest.iconImageURLAndDescription;
			}

			// FPSE-2753 - the server expects unix time, in milliseconds.
			if (dataForUpdateRequest.releaseDate) {
				if (typeof dataForUpdateRequest.releaseDate === 'object') {
					dataForUpdateRequest.releaseDate = dataForUpdateRequest.releaseDate.unix()*1000
				}
			}
			//console.log("prepDataForUpdate end");
		}

		this.submissionCancelled = false;
		// Allow user time to cancel submission on the interstitial overlay
		this.submissionTimeout = setTimeout(() => {
			if (!this.submissionCancelled) {
				return this.newAppUploadStore.uploadEditedScreenshots()
				.then(() => {
					//console.log("Before prepDataForUpdate");
					prepDataForUpdate();
					//console.log("After prepDataForUpdate");
					return apiService.editApp(dataForUpdateRequest)
				})
				.then((result) => {
					//console.log("Edit App Success:", result);
					//console.log("Save ID:", this.newAppData.saveId, "Version:", this.newAppData.version);
					return this.masterStore.appDetailStore.getAppVersions(this.newAppData.applicationId, this.newAppData.version)
				}).then(() => {
					//console.log("Get App Versions Success, updating local info");
					this.newAppData.imageReordered = false;
					this.cloneNewAppData(); //This disables the save dialog by getting the clone up to date
					this.newAppDetailsStore.isValidatingFields = false;
					this.appSuccessfullyEdited = true;
					this.showLoadingOverlay = false;
					history.push(utilsService.generateAppPageURI(this.newAppData, 'view'));
				}).catch(() => {
					//console.error("Error in promise chain:", error);
					this.showLoadingOverlay = false;
					this.appSuccessfullyEdited = false;
				});
			}
		}, 4000);

	}

	@action toggleReadAndAgree(target) {
		this.masterStateProps.userAgreedToTerms = target.checked;
	}

	@action finishGoingToError() {
		this.isGoingToError = false;
	}

	@action getCategoriesAndSegments() {
		this.newAppDetailsStore.getCategoriesAndSegments();
	}

	@action getAppCapabilities() {
		this.newAppDetailsStore.getAppCapabilities();
	}

	// COMPUTEDS
	@computed get newAppFormIsDirty() {
		return !_.isEqual(
			_.omit(toJS(this.newAppData), ['lastSavedDatetime', 'imageReordered']),
			_.omit(toJS(this.newAppDataClone), ['lastSavedDatetime', 'imageReordered'])
		)
	}

	@computed get isAndroidApp() {
		return /android/i.test(this.newAppData.platform);
	}

	@computed get disableMainSection() {
		return this.masterStateProps.namePlatformIsEditable || this.masterStateProps.isSavingAndChecking;
	}

	getDisplayList(checkboxType){
		//console.log('getDisplayList - checkboxType = ', checkboxType);
		if(!this.newAppData[checkboxType] || this.newAppData[checkboxType] === 0) return '';
		return this.newAppData[checkboxType]
		  .filter(item => _.find(this.newAppDetailsStore.checkboxFields[checkboxType].values, [(checkboxType === 'searchTags') ? 'display' : 'name', item]) != undefined)
		  .map(item => {
			if (checkboxType === 'capabilities') {
				item === 'First Priority QoS' && (item = 'App Priority');
				item === 'FirstNet SSO' && (item = 'Single Sign On');
			}
			
			let obj = _.find(this.newAppDetailsStore.checkboxFields[checkboxType].values, [(checkboxType === 'searchTags') ? 'display' : 'name', item])
			// console.log('getDisplayList - item = ', item);
			// console.log('getDisplayList - this.newAppDetailsStore.checkboxFields[checkboxType].values = ', JSON.stringify(this.newAppDetailsStore.checkboxFields[checkboxType].values));
			// console.log('getDisplayList - obj = ', JSON.stringify(obj));
			return obj ? obj.display : '';
		}).join(', ');
	}

	// OBSERVABLES
	@observable masterStateProps = {
		errorsAreDisplayed: false,
		namePlatformIsEditable: true,
		userAgreedToTerms: false,
		isSavingAndChecking: false,
		isPartialSaving: false
	};

	@observable newAppData = this.initNewAppData();
	@observable newAppDataClone = {}; //NOTE: The clone should ALWAYS reflect what's on the server or partially saved, not any intermediate mutations.
	@observable isLoading = false;
	@observable appSuccessfullyEdited = null;
	@observable isGoingToError = false;
	@observable saveAndCheckHasBeenClicked = false;
	@observable showLoadingOverlay = false;
	@observable routeAttemptingToVisit = '';
}
